import { skipToken, useSuspenseQuery } from "@apollo/client";
import dayjs from "dayjs";
import {
    BusinessCode,
    DepartureTimingStatus,
    GcDepartureStatus,
    MyAccountBasicDepartureFragment,
    MyAccountBasicMyTripFragment,
    MyGcDeparturesDocument,
    MyGcDeparturesQuery,
    MyGcDeparturesQueryVariables,
    MyGcDeparturesSuspenseQueryHookResult,
    TripStatus,
    TripTimingStatus,
    useOldTripsSuspenseQuery,
} from "generated/graphql";
import { useLightMe } from "./use-light-me";

export type MyAccountBasicTripAndDeparture = {
    // The Exclude is to avoid people using the trip.departure because everything based on the departure should be departure based.
    trip?: Exclude<MyAccountBasicMyTripFragment, "departure">;
    gcDepartureId?: string;
    departure: MyAccountBasicDepartureFragment;
    tourName?: string;
};

type ValidTrip = MyAccountBasicMyTripFragment & {
    departure: MyAccountBasicDepartureFragment;
};

type ValidNbgc = NonNullable<
    MyGcDeparturesSuspenseQueryHookResult["data"]
>["gcDepartures"]["results"][0] & {
    departure: MyAccountBasicDepartureFragment;
    micrositeContent: { title: string | null | undefined } | null | undefined;
};

/**
 * This method and the below are only exported for use in tests.
 */
export const validTripToMyAccountBasicTripAndDeparture = (
    validTrip: ValidTrip,
): MyAccountBasicTripAndDeparture => {
    return {
        trip: validTrip,
        departure: validTrip.departure,
        gcDepartureId: validTrip.booking?.gcDeparture?._id,
        tourName:
            validTrip.booking?.gcDeparture?.micrositeContent?.title ||
            validTrip.departure.tourContent?.name ||
            undefined,
    };
};

export const validNbgcToMyAccountBasicTripAndDeparture = (
    validNbgc: ValidNbgc,
): MyAccountBasicTripAndDeparture => {
    return {
        gcDepartureId: validNbgc._id as string,
        departure: validNbgc.departure,
        tourName:
            validNbgc.micrositeContent?.title ||
            validNbgc.departure.tourContent?.name ||
            undefined,
    };
};

type UseTripsAndDepartureData = {
    pastTripsAndDepartures: MyAccountBasicTripAndDeparture[];
    upcomingTripsAndDepartures: MyAccountBasicTripAndDeparture[];
    currentTripsAndDepartures: MyAccountBasicTripAndDeparture[];
};

export const useTripsAndDepartures = (): UseTripsAndDepartureData => {
    const { customer } = useLightMe();

    const nbgcDepartures = useNbgcDepartures(
        customer?.hasGCDepartures ? customer._id : undefined,
    );
    if (customer) {
        const {
            confirmedTrips,
            requestedTrips,
            waitlistedTrips,
            pendingRebookingTrips,
        } = customer;

        const allTrips: MyAccountBasicTripAndDeparture[] = [
            ...confirmedTrips,
            ...requestedTrips,
            ...waitlistedTrips,
            ...pendingRebookingTrips,
        ]
            .filter((trip): trip is ValidTrip => !!trip.departure)
            .map(validTripToMyAccountBasicTripAndDeparture);

        const mappedNbgcDepartures: MyAccountBasicTripAndDeparture[] =
            nbgcDepartures.map(validNbgcToMyAccountBasicTripAndDeparture);

        const allTripsAndDepartures = [
            ...allTrips,
            ...mappedNbgcDepartures,
        ].sort((a, b) => {
            const departureDateA =
                a.trip?.departureDate ?? a.departure.departureDate;
            const departureDateB =
                b.trip?.departureDate ?? b.departure.departureDate;

            if (departureDateA === departureDateB) {
                // If two trip departures happen on the same day we should sort them alphabetically by the departure id to avoid hydration issues
                if (a.departure._id === b.departure._id) {
                    // It is at least possible in QA for there to be multiple GC departures for the same departure.
                    return (a.gcDepartureId ?? "") < (b.gcDepartureId ?? "")
                        ? -1
                        : 1;
                }

                return a.departure._id < b.departure._id ? -1 : 1;
            }

            return departureDateA < departureDateB ? -1 : 1;
        });

        const today = dayjs().toISOString();

        const pastTripsAndDepartures = allTripsAndDepartures
            .filter(({ trip, departure }) => {
                if (!!trip) {
                    return (
                        (trip.timingStatus === TripTimingStatus.PostTour &&
                            trip.status === TripStatus.Confirmed) ||
                        trip.endDate < today
                    );
                } else {
                    return (
                        departure.timingStatus ===
                        DepartureTimingStatus.PostTour
                    );
                }
            })
            // Reverse the array so the most recent trip is first
            .reverse();

        const upcomingTripsAndDepartures = allTripsAndDepartures.filter(
            ({ trip, departure }) => {
                if (!!trip) {
                    return (
                        trip.timingStatus === TripTimingStatus.PreTour ||
                        trip.departureDate > today
                    );
                } else {
                    return (
                        departure.timingStatus === DepartureTimingStatus.PreTour
                    );
                }
            },
        );

        const currentTripsAndDepartures = allTripsAndDepartures.filter(
            ({ trip, departure }) => {
                if (!!trip) {
                    return (
                        (trip.timingStatus === TripTimingStatus.OnTour &&
                            trip.status === TripStatus.Confirmed) ||
                        (trip.departureDate < today && trip.endDate > today)
                    );
                } else {
                    return (
                        departure.timingStatus === DepartureTimingStatus.OnTour
                    );
                }
            },
        );

        return {
            pastTripsAndDepartures,
            upcomingTripsAndDepartures,
            currentTripsAndDepartures,
        };
    }
    return {
        pastTripsAndDepartures: [],
        upcomingTripsAndDepartures: [],
        currentTripsAndDepartures: [],
    };
};

export const useNbgcDepartures = (customerId?: string) => {
    const { data } = useSuspenseQuery<
        MyGcDeparturesQuery,
        MyGcDeparturesQueryVariables
    >(
        MyGcDeparturesDocument,
        customerId
            ? {
                  variables: {
                      language:
                          process.env.NEXT_PUBLIC_STORYBLOK_LANGUAGE ?? "",
                      filter: {
                          businessCode: (process.env
                              .NEXT_PUBLIC_BUSINESS_CODE ||
                              BusinessCode.Ub) as BusinessCode,
                          groupCoordinatorCustomerIds: [customerId],
                          statuses: [GcDepartureStatus.Confirmed],
                      },
                  },
              }
            : skipToken,
    );

    return (
        data?.gcDepartures.results.filter(
            (dep): dep is ValidNbgc =>
                dep.isNonBookedGroupCoordinator && !!dep.departure,
        ) ?? []
    );
};

export const useOldTrips = () => {
    const { data } = useOldTripsSuspenseQuery({
        variables: {
            businessCode: process.env.NEXT_PUBLIC_BUSINESS_CODE as BusinessCode,
        },
    });

    if (data?.me?.__typename !== "Customer") {
        return [];
    }

    return data.me.confirmedOldTrips;
};

export const useTripAndDeparture = (
    bookingId: string,
): MyAccountBasicTripAndDeparture | undefined => {
    const {
        pastTripsAndDepartures,
        upcomingTripsAndDepartures,
        currentTripsAndDepartures,
    } = useTripsAndDepartures();
    return (
        pastTripsAndDepartures.find(
            (trip) => trip.trip?.booking?._id === bookingId,
        ) ??
        upcomingTripsAndDepartures.find(
            (trip) => trip.trip?.booking?._id === bookingId,
        ) ??
        currentTripsAndDepartures.find(
            (trip) => trip.trip?.booking?._id === bookingId,
        )
    );
};

export const useNbgcTripAndDeparture = (
    gcDepartureId: string,
): MyAccountBasicTripAndDeparture | undefined => {
    const {
        pastTripsAndDepartures,
        upcomingTripsAndDepartures,
        currentTripsAndDepartures,
    } = useTripsAndDepartures();
    return (
        pastTripsAndDepartures.find(
            (trip) => trip.gcDepartureId === gcDepartureId,
        ) ??
        upcomingTripsAndDepartures.find(
            (trip) => trip.gcDepartureId === gcDepartureId,
        ) ??
        currentTripsAndDepartures.find(
            (trip) => trip.gcDepartureId === gcDepartureId,
        )
    );
};
